import type { TaskSource } from '@netcon-work/shared'
import { useState, type ReactNode } from 'react'
import { cn } from '../../lib/cn'
import { Aktivitaetsfeed } from '../Aktivitaetsfeed'
import { AufgabenVerweis, useAufgabeOeffnen } from '../AufgabeKontext'
import type { Aufgabe } from '../../lib/queries'
import { Button, SectionTitle, SourceBadge } from '../ui/primitives'
import { AufgabenWeltMarke } from '../WorkspaceMarke'

/**
 * **Zone 5 – das Regal unter der Arbeit**
 * ([Z-02](../../../../../docs/phasen/zusatz-02-mein-tag.md),
 * [Z-11](../../../../../docs/phasen/zusatz-11-zusammenfuehren.md)).
 *
 * Drei zugeklappte Zeilen, jede mit ihrer Zahl. Nichts davon ist gelöscht, nichts davon steht im
 * Weg. Das Gegenstück ist Zone 0 „Klopft an" ganz oben – zusammen ergeben sie die Regel, nach der
 * diese Ansicht arbeitet: **was dich braucht, kommt nach oben; was nur da ist, geht nach unten.**
 *
 * Der Aktivitätsfeed stand vorher mitten in der linken Spalte und schob die Aufgaben nach
 * unten. Er ist nicht weniger wert geworden, er beantwortet nur eine andere Frage als *„was
 * mache ich jetzt"*.
 *
 * ## Warum die Zone nicht mehr „Ruhig gestellt" heißt (28.08.2026)
 *
 * Nils: *„«Ruhig gestellt» ist ggf. auch nicht der richtige Begriff … das «was heute keine
 * Entscheidung von dir braucht» würde mir suggerieren – die rücken schon irgendwann oben in
 * «Klopft an». Tun sie aber nie."*
 *
 * Er hat recht, und der Satz war die eigentliche Lüge, nicht das Wort. Was hier liegt, wird
 * **nicht befördert**: der Vorrat wartet, bis jemand zieht, und ein Kommentar an einer fremden
 * Aufgabe steigt nie auf – nur Erwähnungen tun das, und die stehen dann oben, nicht hier
 * ([E-260](../../../../../docs/ENTSCHEIDUNGEN.md#e-260)). „Zum Nachsehen · liegt hier, bis du
 * hinsiehst" verspricht genau so viel, wie die Zone halten kann.
 *
 * ## Und warum jede Zeile jetzt eine Zahl trägt
 *
 * Nils, im selben Zug: *„das man schon in den eingeklappten Gruppen sieht wie viele darin sind,
 * das geht aktuell unten unter."* Vorher trug nur „Wartet auf Abnahme" eine Zahl; „Neu für dich"
 * war zugeklappt von einer leeren Zeile nicht zu unterscheiden. Eine zugeklappte Gruppe **ohne**
 * Zahl ist eine Aufforderung, jeden Tag einmal nachzusehen, ob sich etwas darin versteckt – und
 * genau das tut nach der dritten leeren Klappe niemand mehr.
 */
export function RuhigGestellt({
  wartet,
  laengstesWarten,
  vorratGesamt,
  bestandGesamt,
  neuFuerDich,
  ohneFeed = false,
}: {
  wartet: readonly Aufgabe[]
  laengstesWarten: number | null
  vorratGesamt: number
  bestandGesamt: number
  neuFuerDich: { aufgaben: number; erwaehnungen: number }
  /**
   * **Im Modus „Einfach" steht der Feed nebenan**
   * ([Z-12](../../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
   *
   * Dort trägt die Nachrichtenspalte dieselben Kommentare, nach Erwähnung / Antwort / Gelesen
   * getrennt und mit je einer Zahl. Die zugeklappte Zeile „Neu für dich" wäre daneben dieselbe
   * Auskunft ein zweites Mal – und die einzige Zone, in der man dieselbe Nachricht zweimal
   * abhaken könnte.
   */
  ohneFeed?: boolean
}) {
  const oeffnen = useAufgabeOeffnen()

  return (
    <section>
      <SectionTitle hint="liegt hier, bis du hinsiehst">Zum Nachsehen</SectionTitle>

      <div className="overflow-hidden rounded-card border border-line2">
        <Zeile
          titel="Wartet auf Abnahme"
          zahl={wartet.length}
          /**
           * **Die Erinnerung gehört dem, der sie loswerden kann** (Phase 9). Eine Abnahme
           * wartet auf jemand anderen – aber der Bearbeiter ist der Einzige, der nachfragen
           * kann. Gemessen sind in zwölf Tagen aus 12 wartenden Abnahmen 17 geworden, weil
           * niemand hingesehen hat. Die Warnung steht deshalb **in der Zeile**, nicht in einer
           * eigenen Karte darüber.
           */
          warnung={
            laengstesWarten !== null && laengstesWarten >= 14
              ? `seit ${laengstesWarten} Tagen ohne Abnahme – einmal nachfragen?`
              : null
          }
          dringend={laengstesWarten !== null && laengstesWarten >= 90}
        >
          {wartet.map((a) => (
            <Aufgabenzeile
              key={a.id}
              aufgabe={a}
              onOeffnen={() => oeffnen(a.id)}
              knopf="Nachfragen"
            />
          ))}
        </Zeile>

        {/**
         * **Die Zahl ist hier ein Rückstand und keine Bestandsangabe** – deshalb als Einzige
         * hervorgehoben, sobald sie über null steht. Bei den beiden Nachbarn ist eine große
         * Zahl normal (3.100 Aufgaben sind kein Versäumnis); hier heißt sie: das hat noch
         * niemand gelesen.
         *
         * Die Erwähnungen stehen zusätzlich daneben, weil sie das Einzige in dieser Zone sind,
         * das **auch oben** steht. Wer sie dort abhakt, sieht die Zahl hier fallen – ohne den
         * Zusatz sähe es aus, als hätte das Abhaken nichts bewirkt.
         */}
        {!ohneFeed && (
          <Zeile
            titel="Neu für dich"
            zahl={neuFuerDich.aufgaben}
            note={
              neuFuerDich.erwaehnungen > 0
                ? `${neuFuerDich.erwaehnungen === 1 ? 'eine Erwähnung' : `${neuFuerDich.erwaehnungen} Erwähnungen`} – die stehen oben`
                : 'Kommentare und Erwähnungen'
            }
            hebeZahl={neuFuerDich.aufgaben > 0}
          >
            {/* Der Feed bringt seine eigene Bedienung mit – Abhaken, Filter, Zehn-Minuten-Nachlauf.
                Hier wird er nur eingehängt, nicht nachgebaut. */}
            <div className="-mx-3.5 -mb-2.5">
              <Aktivitaetsfeed />
            </div>
          </Zeile>
        )}

        {/*
          „★ Für mich vorgesehen" steht **nicht** hier: seit Zone 3 den Kopf des Vorrats zeigt
          (★ zuerst), wäre es dieselbe Liste ein zweites Mal. Zwei Zonen, die dasselbe zeigen,
          sind eine Zone zu viel.
        */}
        <Zeile
          titel="Vorrat & Alle Aufgaben"
          zahl={vorratGesamt}
          note={`ziehbar – von ${bestandGesamt} im Bestand`}
        >
          <p className="py-2 text-[12.5px] text-muted">
            Bewusst hinter einem Klick. Wer sich etwas holen will, drückt{' '}
            <b className="text-ink">Aufgabe ziehen</b> – eine Liste mit {bestandGesamt} Zeilen ist
            kein Arbeitsplatz. Der volle Vorrat mit Pool-Filtern steht im Ziehen-Fenster unter
            „Ganzen Vorrat zeigen".
          </p>
        </Zeile>
      </div>
    </section>
  )
}

/**
 * Eine zugeklappte Gruppe.
 *
 * **Die Zahl steht in der Zeile und nicht erst dahinter** (28.08.2026). Vorher stand sie ganz
 * rechts, klein, grau und direkt neben dem Pfeil – und wo `null` stand, blieb dort ein Loch mit
 * einem Leerzeichen. Jetzt trägt sie ein eigenes Feld unmittelbar hinter dem Titel: sie gehört
 * zur Überschrift („Wartet auf Abnahme **4**"), nicht zur Bedienung.
 *
 * **Auch die Null wird gezeigt.** Eine leere Gruppe verschwinden zu lassen wäre hier falsch: die
 * drei Zeilen sind ein festes Regal, und wer sich an ihre Reihenfolge gewöhnt hat, soll sie
 * wiederfinden. Eine gezeigte `0` ist außerdem die einzige Zahl, die man **nicht** aufklappen
 * muss – sie erspart den Klick, statt ihn zu verlangen.
 */
function Zeile({
  titel,
  zahl,
  note,
  warnung,
  dringend,
  hebeZahl,
  children,
}: {
  titel: string
  zahl: number
  note?: string
  warnung?: string | null
  dringend?: boolean
  /** Hebt die Zahl hervor – nur dort, wo sie einen Rückstand misst und keinen Bestand. */
  hebeZahl?: boolean
  children: ReactNode
}) {
  const [offen, setOffen] = useState(false)
  const leer = zahl === 0
  return (
    <>
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-expanded={offen}
        className="flex w-full cursor-pointer items-center gap-2.5 bg-surface px-3.5 py-2.5 text-left text-[13px] text-ink2 not-first:border-t not-first:border-line hover:bg-page"
      >
        <span className="shrink-0">{titel}</span>

        {/*
          `aria-label` statt einer nackten Zahl: vorgelesen ist „Wartet auf Abnahme, 4" eine
          Aufzählung ohne Bezug. Mit Label wird daraus ein Satz.
        */}
        <span
          aria-label={`${zahl} in dieser Gruppe`}
          className={cn(
            'tabular shrink-0 rounded-md px-1.5 py-[1px] text-[11.5px] font-semibold',
            leer && 'text-muted',
            !leer && !hebeZahl && 'bg-page text-ink2',
            !leer && hebeZahl && 'bg-accent-deep text-white',
          )}
        >
          {zahl}
        </span>

        {warnung && (
          <span
            className={`min-w-0 truncate text-xs font-semibold ${dringend ? 'text-pool-sofort' : 'text-warn'}`}
          >
            {warnung}
          </span>
        )}
        {note && !warnung && <span className="min-w-0 truncate text-xs text-muted">{note}</span>}

        <span className="ml-auto shrink-0 text-[11.5px] text-muted">{offen ? '▲' : '▼'}</span>
      </button>
      {offen && (
        <div className="border-t border-line bg-surface px-3.5 pt-0.5 pb-2.5">{children}</div>
      )}
    </>
  )
}

function Aufgabenzeile({
  aufgabe,
  onOeffnen,
  knopf,
}: {
  aufgabe: Aufgabe
  onOeffnen: () => void
  knopf: string
}) {
  return (
    <div className="flex items-center gap-2.5 border-b border-line py-[7px] text-[13px] last:border-b-0">
      <SourceBadge source={aufgabe.sourceId as TaskSource} label={aufgabe.externalKey} />
      <AufgabenVerweis
        aufgabeId={aufgabe.id}
        className="min-w-0 flex-1 cursor-pointer truncate text-left hover:text-accent hover:underline"
        title={aufgabe.title}
      >
        {aufgabe.title}
      </AufgabenVerweis>
      {/** Z-07.8 – auch Wartendes und Geparktes kommt aus mehreren Arbeitswelten. */}
      <AufgabenWeltMarke workspaceId={aufgabe.workspaceId} className="shrink-0" />
      {aufgabe.abnahmeDurchName && (
        <span className="shrink-0 text-[11px] text-muted">bei {aufgabe.abnahmeDurchName}</span>
      )}
      <Button size="sm" variant="ghost" onClick={onOeffnen}>
        {knopf}
      </Button>
    </div>
  )
}
